import React, { useEffect, useMemo, useRef, useState } from 'react'
import './ParentPinModal.css'

// Mirrors PIN_MIN_LENGTH / PIN_MAX_LENGTH in functions/index.js. The callables
// re-validate everything here; these bounds only stop a parent from submitting
// something the server is certain to reject.
const PIN_MIN_LENGTH = 4
const PIN_MAX_LENGTH = 6
const RESET_CODE_LENGTH = 6

const PIN_FIELD = {
  pin: { label: 'PIN', autoComplete: 'off' },
  currentPin: { label: 'Current PIN', autoComplete: 'off' },
  newPin: { label: `New PIN (${PIN_MIN_LENGTH}\u2013${PIN_MAX_LENGTH} digits)`, autoComplete: 'off' },
  confirmPin: { label: 'Confirm new PIN', autoComplete: 'off' },
  code: { label: `${RESET_CODE_LENGTH}-digit code from your email`, autoComplete: 'one-time-code' }
}

// One entry per thing a parent can do with the PIN. Keeping the copy and the
// field list together is what lets a single component serve the exit keypad,
// Settings, and the emailed reset without any of them drifting apart.
const MODES = {
  set: {
    title: 'Set a Student View PIN',
    description: 'Your child will need this PIN to leave Student View. It is the same PIN for every student on your account.',
    fields: ['newPin', 'confirmPin'],
    submitLabel: 'Save PIN',
    busyLabel: 'Saving...'
  },
  verify: {
    title: 'Enter your PIN',
    description: 'Enter your parent PIN to leave Student View.',
    fields: ['pin'],
    submitLabel: 'Leave Student View',
    busyLabel: 'Checking...',
    allowForgot: true
  },
  change: {
    title: 'Change your PIN',
    description: 'Enter your current PIN, then choose a new one.',
    fields: ['currentPin', 'newPin', 'confirmPin'],
    submitLabel: 'Change PIN',
    busyLabel: 'Saving...',
    allowForgot: true
  },
  remove: {
    title: 'Remove your PIN',
    description: 'Without a PIN, leaving Student View only needs a confirmation tap.',
    fields: ['pin'],
    submitLabel: 'Remove PIN',
    busyLabel: 'Removing...',
    allowForgot: true
  },
  reset: {
    title: 'Reset your PIN by email',
    description: 'Enter the code we emailed you, then choose a new PIN.',
    fields: ['code', 'newPin', 'confirmPin'],
    submitLabel: 'Save new PIN',
    busyLabel: 'Saving...',
    allowRemoveInstead: true
  }
}

const digitsOnly = (value, maxLength) =>
  value.replace(/\D/g, '').slice(0, maxLength)

/**
 * The single PIN surface: the exit keypad, the Settings set/change/remove
 * flows, and the emailed reset all render through here.
 *
 * `onSubmit` receives the entered values and the caller decides which callable
 * to invoke, so this component never knows a PIN beyond the keystroke that
 * produced it — nothing is persisted, cached, or logged locally.
 */
const ParentPinModal = ({
  mode,
  busy = false,
  error = null,
  notice = null,
  onSubmit,
  onCancel,
  onForgotPin = null,
  onSendResetCode = null,
  sendingResetCode = false
}) => {
  const config = MODES[mode]
  const [values, setValues] = useState({})
  const [localError, setLocalError] = useState(null)
  const [removeInstead, setRemoveInstead] = useState(false)
  const firstInputRef = useRef(null)

  const fields = useMemo(() => {
    if (!config) return []
    if (mode === 'reset' && removeInstead) {
      return config.fields.filter(field => field === 'code')
    }
    return config.fields
  }, [config, mode, removeInstead])

  useEffect(() => {
    setValues({})
    setLocalError(null)
    setRemoveInstead(false)
  }, [mode])

  useEffect(() => {
    firstInputRef.current?.focus()
  }, [mode])

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !busy) onCancel()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [busy, onCancel])

  if (!config) return null

  const setField = (field, rawValue) => {
    const maxLength = field === 'code' ? RESET_CODE_LENGTH : PIN_MAX_LENGTH
    setValues(previous => ({ ...previous, [field]: digitsOnly(rawValue, maxLength) }))
    setLocalError(null)
  }

  const validate = () => {
    for (const field of fields) {
      const value = values[field] || ''
      if (field === 'code') {
        if (value.length !== RESET_CODE_LENGTH) {
          return `Enter the ${RESET_CODE_LENGTH}-digit code from your email`
        }
        continue
      }
      if (value.length < PIN_MIN_LENGTH || value.length > PIN_MAX_LENGTH) {
        return `${PIN_FIELD[field].label} must be ${PIN_MIN_LENGTH} to ${PIN_MAX_LENGTH} digits`
      }
    }

    if (fields.includes('confirmPin') && values.newPin !== values.confirmPin) {
      return 'Those PINs do not match'
    }

    if (fields.includes('currentPin') && fields.includes('newPin') &&
      values.currentPin === values.newPin) {
      return 'Your new PIN must be different from your current one'
    }

    return null
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    if (busy) return

    const validationError = validate()
    if (validationError) {
      setLocalError(validationError)
      return
    }

    onSubmit({
      pin: values.pin || null,
      currentPin: values.currentPin || null,
      // In reset mode "remove instead" is expressed as a null new PIN, which is
      // exactly what confirmParentPinReset reads as "clear the PIN".
      newPin: removeInstead ? null : (values.newPin || null),
      code: values.code || null,
      removePin: removeInstead
    })
  }

  const shownError = localError || error

  return (
    <div className="parent-pin-overlay" role="presentation">
      <form
        className="parent-pin-modal"
        onSubmit={handleSubmit}
        role="dialog"
        aria-modal="true"
        aria-labelledby="parent-pin-title"
      >
        <div className="parent-pin-icon" aria-hidden="true">🔒</div>
        <h2 id="parent-pin-title" className="parent-pin-title">{config.title}</h2>
        <p className="parent-pin-description">{config.description}</p>

        {notice && (
          <div className="parent-pin-notice" role="status">{notice}</div>
        )}

        {mode === 'reset' && config.allowRemoveInstead && (
          <label className="parent-pin-remove-instead">
            <input
              type="checkbox"
              checked={removeInstead}
              onChange={(event) => {
                setRemoveInstead(event.target.checked)
                setLocalError(null)
              }}
              disabled={busy}
            />
            <span>Remove the PIN instead of setting a new one</span>
          </label>
        )}

        {fields.map((field, index) => (
          <div className="parent-pin-field" key={field}>
            <label className="parent-pin-label" htmlFor={`parent-pin-${field}`}>
              {PIN_FIELD[field].label}
            </label>
            <input
              id={`parent-pin-${field}`}
              ref={index === 0 ? firstInputRef : null}
              className="parent-pin-input"
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete={PIN_FIELD[field].autoComplete}
              maxLength={field === 'code' ? RESET_CODE_LENGTH : PIN_MAX_LENGTH}
              value={values[field] || ''}
              onChange={(event) => setField(field, event.target.value)}
              disabled={busy}
            />
          </div>
        ))}

        {shownError && (
          <div className="parent-pin-error" role="alert">{shownError}</div>
        )}

        <div className="parent-pin-actions">
          <button
            type="submit"
            className="parent-pin-submit"
            disabled={busy}
          >
            {busy ? config.busyLabel : (removeInstead ? 'Remove PIN' : config.submitLabel)}
          </button>
          <button
            type="button"
            className="parent-pin-cancel"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
        </div>

        {config.allowForgot && onForgotPin && (
          <button
            type="button"
            className="parent-pin-forgot"
            onClick={onForgotPin}
            disabled={busy || sendingResetCode}
          >
            {sendingResetCode ? 'Sending code...' : 'Forgot your PIN?'}
          </button>
        )}

        {mode === 'reset' && onSendResetCode && (
          <button
            type="button"
            className="parent-pin-forgot"
            onClick={onSendResetCode}
            disabled={busy || sendingResetCode}
          >
            {sendingResetCode ? 'Sending code...' : 'Send another code'}
          </button>
        )}
      </form>
    </div>
  )
}

export default ParentPinModal
